<script setup lang="ts">
const props = defineProps<{
  facets: any
  total: number
}>()

const route = useRoute()
const { t, locale } = useI18n()
const { changeFilters, getFacetsFromURL } = useSiteUrl()
const showPopup = ref(false)
const selectedFilters = ref<SelectedProductFilters>({ ...getFacetsFromURL().filters })
const filterResetKey = ref(0)
const gaOptions = inject('gaOptions', {
  group: 'plp',
})

const facetsWithSelectedOptions = computed(() => {
  return getFacetsWithSelectedOptions(props.facets, selectedFilters.value)
})

const validFilterByItems = (item: ProductFilter): boolean => {
  if (!item) {
    return false
  }
  if (item.id === 'price') {
    return true
  }
  return item.options.length > 0
}

const mapCheckboxOptions = (options: ProductFilterOption[]) => {
  const activeOptions = options.reduce((filtered: any, option: ProductFilterOption) => {
    const { label, value, count, attrName, selected } = option
    if (count === undefined) {
      filtered.push({
        label,
        value,
        attrName,
        selected,
      })
    } else if (count > 0) {
      filtered.push({
        label: `${label} (${count})`,
        value,
        attrName,
        selected,
      })
    }
    return filtered
  }, [])
  return activeOptions
}

const optionsRange = (item: ProductFilter) => {
  const optionRangeId = item?.options?.[0]?.id ? REGEX_RANGE_FORMAT.exec(item.options[0].id) : null
  const optionRangeValue = item?.options?.[0]?.value ? REGEX_RANGE_FORMAT.exec(item.options[0].value) : null
  const optionRangeIdNumber = optionRangeId ? getMinMaxFromRangeFormat(optionRangeId) : [0]
  const optionRangeValueNumber = optionRangeValue ? getMinMaxFromRangeFormat(optionRangeValue) : [0]
  return { id: optionRangeIdNumber, value: optionRangeValueNumber }
}

const handleFilterChange = async (id: string, value: string, newState: boolean) => {
  const query = JSON.parse(JSON.stringify(route.query))

  if (id === 'price') {
    if (newState) {
      query[id] = value
    } else {
      delete query[id]
    }
  } else {
    const current = queryStrings(query[id])
    const next = newState
      ? current.includes(value)
        ? current
        : [...current, value]
      : current.filter((v) => v !== value)

    if (next.length === 0) {
      delete query[id]
    } else {
      // A single value stays a bare string so the URL reads `?id=value` rather than a one-item array.
      query[id] = next.length === 1 ? next[0] : next
    }
  }

  const params = new URLSearchParams()

  Object.entries(query).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      values.forEach((val) => {
        params.append(key, val)
      })
    } else {
      params.append(key, String(values))
    }
  })

  // Force URL update with the full, manually constructed URL to bypass Vue Router's internal query parameter handling.
  // This is to avoid issues caused by the handling of array serialization by Vue Router.
  const fullPath = `${route.path}?${params.toString()}`

  await navigateTo(fullPath, { replace: true })

  selectedFilters.value = { ...getFacetsFromURL().filters }
}

const clearFilters = (location: 'inside' | 'outside') => {
  selectedFilters.value = {}
  changeFilters({}, 0)
  filterResetKey.value++
  pushEventToDataLayer('custom.filters', {
    ...gaOptions,
    action: 'clear all',
    info: { location },
  })
}

const filterCount = (id: string) => {
  if (!selectedFilters.value || !selectedFilters.value[id]) return 0
  const filters = facetsWithSelectedOptions.value.find((v) => v.id === id)?.options.filter((option) => option.selected)
  return Array.isArray(filters) ? filters.length : 1
}

const handleToggleFilters = (show: boolean) => {
  showPopup.value = show
  pushEventToDataLayer('custom.filters', {
    ...gaOptions,
    action: show ? 'open' : 'apply',
  })
}

const handleFilterChipRemove = (id: string, value: string) => {
  handleFilterChange(id, value, false)
  pushEventToDataLayer('custom.filters.chip', {
    ...gaOptions,
    action: 'clear single',
    name: id,
    label: value,
    info: {
      // filter chips no-longer inside of filter modal, no need to account for them
      location: 'outside',
    },
  })
}
</script>

<template>
  <div class="filter">
    <div class="filter__header">
      <span class="sr-only">{{ t('screen_reader.buttons.product_filter_menu') }}</span>
      <Button variant="none" class="filter__open" data-testid="filter-open" @click="handleToggleFilters(true)">
        <svg-icon name="mhj:symbol-filter" />
        <span>{{ t('plp.filter.menu.list.title.filter') }}</span>
      </Button>
      <span v-if="total > 0" class="filter__total-products" data-testid="filter-total-products">
        {{ total }} {{ t('plp.filter.icon.total_products_text') }}
      </span>
    </div>
    <div v-if="Object.keys(selectedFilters).length > 0" class="filter__selected-filters">
      <template v-for="item in facetsWithSelectedOptions" :key="item.id">
        <template v-if="item.id === 'price' && selectedFilters[item.id]">
          <Button
            v-for="value in selectedFilters[item.id]"
            class="filter__selected-value"
            data-testid="filter-chip"
            variant="none"
            @click="handleFilterChipRemove(item.id, value)"
          >
            {{ formatPriceFilter(value, locale === 'fr') }}
            <svg-icon name="mhj:close" />
          </Button>
        </template>
        <template v-else>
          <Button
            v-for="option in item.options.filter((option) => option.selected)"
            :key="option.id"
            class="filter__selected-value"
            data-testid="filter-chip"
            variant="none"
            @click="handleFilterChipRemove(item.id, option.value)"
          >
            {{ option.label }}
            <svg-icon name="mhj:close" />
          </Button>
        </template>
      </template>
      <Button class="filter__clear-all" data-testid="filter-clear-all" variant="none" @click="clearFilters('outside')">
        {{ t('plp.filter.menu.header.button.clear') }}
      </Button>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <Modal
          class="filter__modal"
          data-testid="filter-modal"
          :show="showPopup"
          @close="showPopup = false"
          :modal-heading="t('plp.filter.menu.list.title.filter')"
          :slide-left="true"
        >
          <template #header>
            <h2 class="modal__heading">{{ t('plp.filter.menu.list.title.filter') }}</h2>
            <Button
              v-if="Object.keys(selectedFilters).length > 0"
              variant="tertiary"
              @click="clearFilters('inside')"
              class="filter__clear-inside"
              data-testid="filter-clear-all-inside"
            >
              {{ t('plp.filter.menu.header.button.clear') }}
            </Button>
          </template>
          <Accordion
            v-for="item in facetsWithSelectedOptions.filter(validFilterByItems)"
            :key="item.id + filterResetKey"
            :title="item.label"
            :test-id="`filter-group-${item.id}`"
            :panel-test-id="`filter-panel-${item.id}`"
            :eventName="'custom.filters.group'"
            :gaOptions="{ ...gaOptions, action: 'open' }"
          >
            <template #title>
              <span>{{ item.label }}</span>
              <span v-if="item.id !== 'price' && filterCount(item.id) > 0"> ({{ filterCount(item.id) }})</span>
            </template>
            <template v-for="option in mapCheckboxOptions(item.options)" :key="option.value">
              <div v-if="item.id === 'price'">
                <RangeSelector
                  :id="item.id"
                  :range="optionsRange(item)"
                  @change="
                    (payload) => handleFilterChange(payload.id, payload.value, payload.value !== item?.options?.[0]?.id)
                  "
                />
              </div>
              <Checkbox
                v-else
                :label="option.label"
                :test-id="`filter-option-${item.id}-${option.value}`"
                :name="option.attrName"
                :checked="option.selected"
                :value="option.value"
                :v-model="`${item.id}_${option.value}`"
                v-bind="
                  ['metalColour', 'gemstone', 'gemstoneColour'].includes(item.id)
                    ? {
                        swatch: {
                          id: item.id,
                          name: item.id,
                          value: option.value,
                        },
                      }
                    : {}
                "
                @update:model-value="
                  (newVal) => {
                    handleFilterChange(item.id, option.value, newVal)
                    pushEventToDataLayer('custom.filters.checkbox', {
                      ...gaOptions,
                      action: newVal ? 'check' : 'uncheck',
                      name: item.id,
                      label: option.label,
                    })
                  }
                "
              />
            </template>
          </Accordion>

          <template #footer>
            <Button
              variant="primary"
              class="filter__view-button"
              data-testid="filter-view-products"
              @click="handleToggleFilters(false)"
            >
              {{
                total
                  ? t('plp.filter.menu.list.button.view_products_with_count', { total })
                  : t('plp.filter.menu.list.button.view_products')
              }}
            </Button>
          </template>
        </Modal>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style lang="postcss">
.filter {
  padding-bottom: var(--space-50);

  .filter__header {
    display: flex;
    gap: var(--space-30);
    align-items: center;

    .filter__total-products {
      font-size: var(--text-small);
      color: var(--text-secondary);
      padding-top: var(--space-20);
    }
  }

  .filter__open {
    display: flex;
    gap: var(--space-30);
    align-items: center;

    &:hover {
      span {
        text-decoration: underline;
      }
    }
  }

  .filter__selected-filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--space-30);
    padding-top: var(--space-40);
    padding-right: var(--space-60);
    position: relative;
    width: calc(100vw - var(--space-40));
    left: 50%;
    margin-left: -50%;
    margin-right: -100vw;

    button {
      font-size: var(--text-small);
      font-weight: bold;
      padding: var(--space-20) var(--space-30);
      text-transform: uppercase;

      &.filter__selected-value {
        display: flex;
        align-items: center;
        gap: var(--space-30);
        background-color: var(--surface-light-40);

        svg.icons {
          height: var(--space-50);
        }
      }

      &.filter__clear-all {
        background-color: var(--surface-light-20);
      }
    }
  }
}

.filter__modal {
  .modal__heading {
    margin: 0;
    padding-left: var(--space-60);
    padding-right: var(--space-50);
  }

  .filter__clear-inside {
    display: flex;
    gap: var(--space-30);
    align-items: center;

    &:hover {
      span {
        text-decoration: underline;
      }
    }
  }

  .accordion {
    summary {
      font-size: var(--text-base);
    }

    .checkbox {
      padding-block: var(--space-30);
    }
  }

  .filter__view-button {
    width: 100%;
  }
}
</style>
